import { R4 } from "@ahryman40k/ts-fhir-types";
import cloneDeep from "lodash/cloneDeep";

import { ConfidentialityCode } from "@/global";

import { FHIR_EXT_CONFIDENTIALITY } from "../constants";
import { VaccinationResource, vaccinationResourceTypes } from "../types";
import { removeDuplicateVaccinationResources } from "./deduplication";
import {
  deleteMetaExtensions,
  getRandomIdentifier,
  getResourceByReference,
  getSectionByCode,
  getUuidUrnReferenceValue,
  isDeleted,
  isVaccinationResource,
  makeEntry,
  setCompositionUuidExtension,
  setConfidentiality,
  setMetaExtension,
} from "./fhir";
import { addReplacedByExtensions, isReplaced } from "./replacements";

export async function aggregateVaccinationRecord(bundles: R4.IBundle[]): Promise<R4.IBundle> {
  const vaccinationRecord = mergeImmunizationDocuments(bundles);
  addReplacedByExtensions(vaccinationRecord);
  await removeDuplicateVaccinationResources(vaccinationRecord);
  return vaccinationRecord;
}

export function mergeImmunizationDocuments(bundles: R4.IBundle[]): R4.IBundle {
  const mergedBundle = createEmptyVaccinationRecord();

  const sectionCodes = ["11369-6", "11450-4", "11348-0", "48765-2", "18727-8"];
  const mergedSectionsByCode: Record<string, any> = {};
  sectionCodes.forEach((code) => {
    mergedSectionsByCode[code] = getSectionByCode(mergedBundle, code);
  });

  for (const bundle of bundles) {
    replaceFhirBundleIdsWithUUIDs(bundle);

    const composition = bundle.entry?.[0].resource as R4.IComposition;
    if (!composition.type.coding?.some((coding) => coding.code === "41000179103")) {
      continue;
    }

    const confidentialityExt = composition._confidentiality?.extension?.[0];

    // Merge bundle entries
    if (bundle.entry && mergedBundle.entry) {
      mergedBundle.entry.push(
        ...bundle.entry
          // Skip empty entries, compositions and binaries
          .filter(
            (entry) =>
              entry.resource && !["Composition", "Binary"].includes(entry.resource.resourceType)
          )
          // Add relevant composition data as meta extension
          .map((entry) => {
            if (!isVaccinationResource(entry.resource) || !confidentialityExt) {
              return entry;
            }

            const resource = entry.resource as VaccinationResource;
            const resourceClone = cloneDeep(resource);
            setMetaExtension(resourceClone, confidentialityExt);
            return { ...entry, resource: resourceClone };
          })
      );
    }

    // Merge section entries
    composition.section?.forEach((section) => {
      const sectionCode = section.code?.coding?.[0].code;
      if (!sectionCode || !section.entry || !mergedSectionsByCode[sectionCode]) {
        return;
      }
      mergedSectionsByCode[sectionCode].entry.push(...section.entry);
    });
  }

  return mergedBundle;
}

export function createEmptyVaccinationRecord(): R4.IBundle {
  const now = new Date().toISOString();

  const composition: R4.IComposition = {
    resourceType: "Composition",
    id: crypto.randomUUID(),
    meta: {
      profile: [
        "http://fhir.ch/ig/ch-vacd/StructureDefinition/ch-vacd-composition-vaccination-record",
      ],
    },
    language: "en-US",
    identifier: getRandomIdentifier(),
    status: R4.CompositionStatusKind._final,
    date: now, // TODO: set on export?
    title: "Vaccination Record",
    type: {
      coding: [
        {
          system: "http://snomed.info/sct",
          code: "41000179103",
          display: "Immunization record",
        },
      ],
    },
    category: [
      {
        coding: [
          {
            system: "urn:oid:2.16.756.5.30.1.127.3.10.10",
            code: "urn:che:epr:ch-vacd:vaccination-record:2022",
            display: "CH VACD Vaccination Record",
          },
        ],
      },
    ],
    author: [],
    section: [
      {
        title: "Immunization administration",
        code: {
          coding: [
            {
              system: "http://loinc.org",
              code: "11369-6",
              display: "Hx of Immunization",
            },
          ],
        },
        entry: [],
      },
      {
        title: "Past illnesses",
        code: {
          coding: [
            {
              system: "http://loinc.org",
              code: "11348-0",
              display: "Hx of Past illness",
            },
          ],
        },
        entry: [],
      },
      {
        title: "Allergies",
        code: {
          coding: [
            {
              system: "http://loinc.org",
              code: "48765-2",
              display: "Allergies and adverse reactions Document",
            },
          ],
        },
        entry: [],
      },
      {
        title: "Medical problems",
        code: {
          coding: [
            {
              system: "http://loinc.org",
              code: "11450-4",
              display: "Problem list Reported",
            },
          ],
        },
        entry: [],
      },
      {
        title: "Laboratory findings",
        code: {
          coding: [
            {
              system: "http://loinc.org",
              code: "18727-8",
              display: "Serology studies (set)",
            },
          ],
        },
        entry: [],
      },
    ],
  };

  setConfidentiality(composition, ConfidentialityCode.Normal);

  return {
    resourceType: "Bundle",
    id: crypto.randomUUID(),
    meta: {
      profile: [
        "http://fhir.ch/ig/ch-vacd/StructureDefinition/ch-vacd-document-vaccination-record",
      ],
    },
    identifier: getRandomIdentifier(),
    type: R4.BundleTypeKind._document,
    timestamp: now, // TODO: set on export?
    entry: [makeEntry(composition)],
  };
}

export function replaceFhirBundleIdsWithUUIDs(bundle: R4.IBundle): R4.IBundle {
  if (bundle.resourceType !== "Bundle" || !bundle.entry) {
    throw new Error("Invalid FHIR bundle");
  }

  const idMap = new Map<string, string>();

  const composition = bundle.entry[0].resource as R4.IComposition;
  if (composition.resourceType !== "Composition") {
    throw new Error("First entry must be a composition");
  }

  const compositionUuid = composition.identifier?.value;

  // 1. Generate new UUIDs and update resource IDs
  for (const entry of bundle.entry) {
    // Skip entries without resource
    if (!entry.resource) {
      continue;
    }

    // Add composition extension
    const resourceType = entry.resource.resourceType;
    if (vaccinationResourceTypes.includes(resourceType) && compositionUuid) {
      setCompositionUuidExtension(entry.resource, compositionUuid);
    }

    // Add resource.id if missing to ensure proper referencing
    if (!entry.resource.id) {
      entry.resource.id = crypto.randomUUID();
    }

    // Add required entry.fullUrl if missing to ensure proper referencing
    // https://fhir.ch/ig/ch-core/2.1.0/StructureDefinition-ch-core-document-epr.html
    if (!entry.fullUrl) {
      entry.fullUrl = `urn:uuid:${entry.resource.id}`;
    }

    const newId = crypto.randomUUID();
    const newRef = `urn:uuid:${newId}`;
    const oldRef = entry.fullUrl.startsWith("urn:uuid:")
      ? entry.fullUrl
      : `${resourceType}/${entry.resource.id}`;
    idMap.set(oldRef, newRef);
    entry.fullUrl = newRef;
    entry.resource.id = newId;
  }

  // 2. Update references recursively
  const replaceRefs = (obj: any) => {
    if (Array.isArray(obj)) {
      obj.forEach(replaceRefs);
    } else if (obj && typeof obj === "object") {
      for (const key in obj) {
        if (key === "reference" && typeof obj[key] === "string") {
          const ref = obj[key];
          if (idMap.has(ref)) {
            obj[key] = idMap.get(ref);
          }
        } else {
          replaceRefs(obj[key]);
        }
      }
    }
  };

  for (const entry of bundle.entry) {
    replaceRefs(entry.resource);
  }

  return bundle;
}

/**
 * Prepares a vaccination record for export by removing invalid entries
 * (deleted, replaced) and custom extensions.
 * @param bundle The CH VACD Vaccination Record document bundle to prepare.
 */
export function prepareVaccinationRecordForExport(bundle: R4.IBundle) {
  if (bundle.resourceType !== "Bundle" || !bundle.entry) {
    throw new Error("Invalid FHIR bundle");
  }

  const bundleClone = cloneDeep(bundle);

  bundleClone.entry = bundleClone.entry!.filter((entry) => {
    if (!entry.resource) {
      return false;
    }

    if (!vaccinationResourceTypes.includes(entry.resource.resourceType)) {
      return true;
    }

    const resource = entry.resource as VaccinationResource;
    return !(isDeleted(resource) || isReplaced(resource));
  });

  // Remove unreferenced entries
  const references = collectReferences(bundleClone);
  bundleClone.entry = bundleClone.entry.filter((entry, i) => {
    if (i === 0) {
      return true;
    }
    const ref = entry.fullUrl?.startsWith("urn:uuid:")
      ? entry.fullUrl
      : getUuidUrnReferenceValue(entry.resource!);
    return !!ref && references.has(ref);
  });

  // Remove dead composition section entry references
  const entryReferences = new Set(
    bundleClone.entry.map((entry) => getUuidUrnReferenceValue(entry.resource!))
  );
  const composition = bundleClone.entry[0].resource as R4.IComposition;
  composition.section?.forEach((section) => {
    section.entry = section.entry?.filter(
      (entry) => entry.reference && entryReferences.has(entry.reference)
    );
  });

  // Remove empty composition sections
  composition.section = composition.section?.filter((section) => section.entry?.length);

  // Remove custom extensions
  composition.section?.forEach((section) => {
    section.entry?.forEach((entry) => {
      const ref = entry.reference;
      const resource = getResourceByReference(bundleClone, ref!);
      deleteMetaExtensions(resource);
    });
  });

  return bundleClone;
}

function collectReferences(obj: any, references: Set<string> = new Set()): Set<string> {
  if (Array.isArray(obj)) {
    for (const item of obj) {
      collectReferences(item, references);
    }
  } else if (obj && typeof obj === "object") {
    for (const [key, value] of Object.entries(obj)) {
      if (key === "reference" && typeof value === "string") {
        references.add(value);
      } else {
        collectReferences(value, references);
      }
    }
  }

  return references;
}

export function getConfidentialityCode(
  resource: VaccinationResource
): ConfidentialityCode | undefined {
  const confidentialityExt = resource.meta?.extension?.find(
    (ext) => ext.url === FHIR_EXT_CONFIDENTIALITY
  );
  if (confidentialityExt) {
    const code = confidentialityExt.valueCodeableConcept?.coding?.[0]?.code;
    return code as ConfidentialityCode;
  }
  return undefined;
}
